import { createContext, useEffect, useState } from 'react';
import { OrderStatusEnum } from '../types/OrderStatusEnum';
import { DateFilterEnum } from '../types/DateFilterEnum';
import moment from 'moment';
import useDatabase from '../hooks/useDatabase';
import useLocalStorage from '../hooks/useLocalStorage';
import useApi from '../hooks/useApi';
import { PaymentStatusEnum } from '../types/PaymentStatusEnum';

export interface AdminContextProps {
  orderFilter: any;
  setOrderFilter: React.Dispatch<React.SetStateAction<any>>;
  orders: any;
  formatOrders: any;
  selectedOrder: any;
  setSelectedOrder: React.Dispatch<React.SetStateAction<any>>;
  selectedRowIndex: number | null;
  setSelectedRowIndex: React.Dispatch<React.SetStateAction<number | null>>;
  orderUploads: any;
  orderUploadsLoading: boolean;
  selectedOrderStatus: OrderStatusEnum;
  setSelectedOrderStatus: React.Dispatch<
    React.SetStateAction<OrderStatusEnum | null>
  >;
  updateNewOrderStatus: any;
  adminContextClear: any;
  isPaymentApproved: () => boolean;
  TrackingshippingCode: (trackingType: number, shippingCode: string) => void;
  downloadOrderZip: (orderId: string) => Promise<void>;
}

export const AdminContext = createContext(null);

export const AdminContextProvider = ({ children }: any) => {
  const { updateTable, downloadOrderZip } = useApi();

  const {
    getCollectionByOrderId,
    loading: orderUploadsLoading,
    getCollectionByTrackingCode,
  } = useDatabase();

  const { getLocalStorageJson, setLocalStorageJson } = useLocalStorage();

  const [orderFilter, setOrderFilter] = useState<any>({
    checkedStatus: [
      OrderStatusEnum.PENDIENTE,
      OrderStatusEnum.PROCESSO,
      OrderStatusEnum.TERMINADO,
      OrderStatusEnum.ENTREGADO,
    ],
    checkedDate: DateFilterEnum.HOY,
  });
  const [orders, setOrders] = useState<any>([]);
  const [selectedOrder, setSelectedOrderState] = useState<any>(
    getLocalStorageJson('admin:selectedOrder') ?? null
  );
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [orderUploads, setOrderUploads] = useState<any>(null);

  const [selectedOrderStatus, setSelectedOrderStatus] = useState<any>(
    selectedOrder ? selectedOrder.id_status : null
  );

  useEffect(() => {
    if (selectedOrder) {
      getCollectionByOrderId('upload', selectedOrder.id).then(setOrderUploads);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrder]);

  const adminContextClear = () => {
    setOrderFilter({
      checkedStatus: [
        OrderStatusEnum.PENDIENTE,
        OrderStatusEnum.PROCESSO,
        OrderStatusEnum.TERMINADO,
        OrderStatusEnum.ENTREGADO,
      ],
      checkedDate: DateFilterEnum.HOY,
    });
    setOrders([]);
    setSelectedOrderState(getLocalStorageJson('admin:selectedOrder') ?? null);
    setSelectedRowIndex(null);
    setOrderUploads(null);
    setSelectedOrderStatus(selectedOrder ? selectedOrder.id_status : null);
  };

  const setSelectedOrder = (order: any) => {
    setLocalStorageJson('admin:selectedOrder', order);
    setSelectedOrderState(order);
  };

  const formatOrders = (orderToFormat: any) => {
    setOrders(
      orderToFormat.map((item: any) => {
        const user_data = JSON.parse(item.user_data);
        const address_send = item.address_send
          ? JSON.parse(item.address_send)
          : item.address_send;
        return {
          ...item,
          user_data,
          address_send,
          name: user_data.name,
          create_time: item.create_time,
          create_date: moment(item.create_time).format('DD/MM/YYYY HH:mm'),
          addressType: item.id_sucursal ? 'Retiro por local' : 'Envío',
          notes: item.notes,
          shipping_code: item.shipping_code,
        };
      })
    );
  };

  const TrackingshippingCode = (trackingType: number, shippingCode: string) => {
    if (trackingType === 1) {
      window.location.href = `https://app.entregarweb.com/tracking/${shippingCode}`;
    } else {
      window.location.href = `https://app.entregarweb.com/api/storage/shipping_label/${shippingCode}.pdf`;
    }
  };

  const isPaymentApproved = () => {
    return selectedOrder.payment_status == PaymentStatusEnum.APROBADO;
  };

  const updateNewOrderStatus = async () => {
    try {
      await updateTable('order', {
        id: selectedOrder.id,
        id_status: selectedOrderStatus,
      });

      const selectedOrderUpdate = await getCollectionByTrackingCode(
        'view_order',
        selectedOrder.tracking_code
      );

      if (selectedOrderUpdate && selectedOrderUpdate.length > 0) {
        const orderData = selectedOrderUpdate[0];
        const updatedOrder = {
          ...selectedOrder,
          shipping_code: orderData.shipping_code,
          id_status: orderData.id_status,
        };
        setSelectedOrderState(updatedOrder);

        localStorage.setItem(
          'admin:selectedOrder',
          JSON.stringify(updatedOrder)
        );
      }
    } catch (error) {
      console.error('Error al obtener o actualizar el pedido:', error);
    }
  };

  // Global State like "Redux"
  const contextProps: AdminContextProps = {
    orderFilter,
    setOrderFilter,
    orders,
    formatOrders,
    selectedOrder,
    setSelectedOrder,
    selectedRowIndex,
    setSelectedRowIndex,
    orderUploads,
    orderUploadsLoading,
    selectedOrderStatus,
    setSelectedOrderStatus,
    updateNewOrderStatus,
    adminContextClear,
    isPaymentApproved,
    TrackingshippingCode,
    downloadOrderZip,
  };

  return (
    // @ts-ignore
    <AdminContext.Provider value={contextProps}>
      {children}
    </AdminContext.Provider>
  );
};
